import { compact } from 'lodash'
import { createStore, applyMiddleware } from 'redux'
import { persistStore, persistReducer, createMigrate, KEY_PREFIX } from 'redux-persist'
import createSagaMiddleware from 'redux-saga'
import { composeWithDevTools } from 'redux-devtools-extension'
import { routerMiddleware as createRouterMiddleware } from 'connected-react-router'
import { datadogRum } from '@datadog/browser-rum-slim'
import { rehydrateKeyPrefix } from 'common/constants'

import { rootSaga } from 'sagas'
import { rootReducer } from 'reducers'
import migrations from 'reducers/persistMigrations'
import { history } from 'lib/history'
import { AuthTransform } from './transforms/auth'
import storage from 'lib/consentStorage'
import * as safeLocalStorage from 'lib/safeLocalStorage'

const sagaMiddleware = createSagaMiddleware()
const routerMiddleware = createRouterMiddleware(history)

const middleware = compact([sagaMiddleware, routerMiddleware])

const persistConfig = {
  key: rehydrateKeyPrefix,
  storage,
  version: 8, // when making changes to the reducers below, make sure to increment version and write a migration for this version otherwise persisted store will be returned
  whitelist: [
    'auth',
    'platform',
    'routing',
    'form',
    'config',
    'joyride',
    'infoBanner',
    'preserveFeatureVisibility',
  ],
  migrate: createMigrate(migrations), // see https://github.com/rt2zz/redux-persist/blob/master/docs/migrations.md
  transforms: [AuthTransform],
}

// Seed the persist key so AuthTransform runs during rehydration.
// Without this, the "transforms" for persisting redux state will not run
// and the oauthData that comes from OneHost (via safeLocalStorage) will be
// wiped out. This has to run before `persistReducer` otherwise it will
// be set too late.
// Note: cross-tab auth sync (src/sagas/auth.js) watches
// syft2webclientauth, which is never written — likely dead code.
const PERSIST_KEY = `${KEY_PREFIX}${rehydrateKeyPrefix}`
if (!safeLocalStorage.getItem(PERSIST_KEY)) {
  safeLocalStorage.setItem(PERSIST_KEY, JSON.stringify({ auth: '{}' }), {
    consentGroup: safeLocalStorage.ConsentGroup.StrictlyNecessary,
  })
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

export const store = createStore(persistedReducer, composeWithDevTools(applyMiddleware(...middleware)))

export const persistor = persistStore(store)

sagaMiddleware.run(rootSaga)
